import { useRef, useState, useCallback, MouseEvent } from 'react';

interface UseHorizontalScrollOptions {
  onScroll?: (scrollLeft: number) => void;
}

const DRAG_THRESHOLD = 5;

export function useHorizontalScroll<T extends HTMLElement>(options: UseHorizontalScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [preventClick, setPreventClick] = useState(false);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!ref.current) return;

    setIsDragging(true);
    setStartX(e.pageX - ref.current.offsetLeft);
    setScrollLeft(ref.current.scrollLeft);
    setPreventClick(false);
    ref.current.style.userSelect = 'none';
  }, []);

  const onMouseMove = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      if (!isDragging || !ref.current) return;

      e.preventDefault();
      const x = e.pageX - ref.current.offsetLeft;
      const walk = (x - startX) * 1.0;

      const currentDragDistance = Math.abs(x - startX);
      if (currentDragDistance > DRAG_THRESHOLD) {
        setPreventClick(true);
      }

      ref.current.scrollLeft = scrollLeft - walk;

      if (options.onScroll) {
        options.onScroll(ref.current.scrollLeft);
      }
    },
    [isDragging, startX, scrollLeft, options]
  );

  const onMouseUp = useCallback(() => {
    if (!ref.current) return;

    setIsDragging(false);
    ref.current.style.removeProperty('user-select');

    if (options.onScroll && ref.current) {
      options.onScroll(ref.current.scrollLeft);
    }
  }, [options]);

  const onMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      onMouseUp();
    }
  }, [isDragging, onMouseUp]);

  return {
    dragProps: {
      ref,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onMouseLeave
    },
    isDragging,
    preventClick
  };
}
